import {
  ActionIcon,
  Badge,
  Box,
  createStyles,
  Group,
  Menu,
  Pagination,
  Paper,
  Skeleton,
  Table,
  Text,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconChevronDown,
  IconChevronRight,
  IconDots,
  IconDragDrop,
  IconEye,
  IconTrash,
} from "@tabler/icons-react";
import {
  useTable,
  useSortBy,
  usePagination,
  useFilters,
  useGlobalFilter,
  useExpanded,
  useGroupBy,
} from "react-table";
import Th from "core/components/table/Thead";
import { useScrollTracker } from "core/hooks/useScrollTracker";
import { SampleEventType } from "core/models/SampleModel";
import { useEffect, useRef, useState } from "react";
import { AppConstants } from "utils/constants";
import { getCommonPinningStyles } from "utils/helper";
import { logger } from "utils/logger";

const SCROLL_REF = "globallist-scrollPos";

const useStyles = createStyles((theme) => ({
  tablecell: {
    padding:13,
    borderRight: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]} !important`,
    // boxShadow: `inset 0 0 0 1px ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]} !important`,
  },
  firstColumn: {
    borderRight: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]} !important`,
    boxShadow: `gray -4px 0px 4px -4px inset`
  }
}));

const MastersList = ({
  leftColumnPinning,
  originalData,
  data,
  onItemClick,
  onMenuItemClick,
  onStatusClick,
  onItemSelected,
  selectedItems,
  filter,
  actionMenu,
  columns,
  rowMenuItems,
  showPopover = false,
  allowAssignCapture = false,
  totalCount = 0,
  loading = false,
  paginationSize = 100,
  setFilter,
  showPagination = true,
}) => {
  const [grouping, setGrouping] = useState([]);
  const [expanded, setExpanded] = useState({ ...filter?.groupExpansion });
  logger.log(filter);

  const [pagination, setPagination] = useState({
    pageIndex: filter?.pageIndex,
    pageSize: 50,
  });

  useEffect(() => {
    logger.log(pagination);
    if (
      pagination?.pageIndex !== filter?.pageIndex ||
      pagination?.pageSize !== filter?.pageSize
    ) {
      setPagination({
        pageIndex: filter?.pageIndex,
        pageSize: filter?.pageSize,
      });
    }
  }, [filter?.pageIndex, filter?.pageSize]);

  useEffect(() => {
    if (filter?.groupBy?.length) {
      setGrouping([filter?.groupBy]);
    } else {
      setGrouping([]);
    }
  }, [filter?.groupBy]);

  useEffect(() => {
    if (setFilter) setFilter({ ...filter, groupExpansion: { ...expanded } });
  }, [expanded]);

  const tableProps = {
    columns,
    data,
    grouping,
    setGrouping,
    loading,
    selectedItems,
    pagination,
    setPagination,
    onItemClick,
    showPagination,
    expanded,
    setExpanded,
    originalData,
    setFilter,
    filter,
  };

  const divRef = useRef();
  const { scrolTop } = useScrollTracker(divRef, "wiplist");

  useEffect(() => {
    const scrollPosition = localStorage.getItem(SCROLL_REF);
    if (scrollPosition) {
      divRef.current.scrollTo(0, parseInt(scrollPosition, 10));
    } else {
      divRef.current.scrollTo(0, 0);
    }
  }, []);

  return (
    <Box
      ref={divRef}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <CustomTable {...tableProps} />
    </Box>
  );
};

const CustomTable = ({
  columns,
  data,
  grouping,
  setGrouping,
  loading,
  selectedItems,
  onItemClick,
  onMenuItemClick,
  onItemSelected,
  onStatusClick,
  actionMenu,
  showPopover,
  allowAssignCapture,
  pagination,
  setPagination,
  showPagination,
  expanded,
  setExpanded,
  originalData,
  setFilter,
  filter,
}) => {
  const theme = useMantineTheme();
  const mobileMatch = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const { classes } = useStyles();
  // v7: initialState controls pageIndex/pageSize, groupBy, expanded
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    // pagination
    page,
    pageCount,
    state: { pageIndex, pageSize, groupBy, expanded: tableExpanded },
    gotoPage,
    setPageSize,
    // grouping
    setGroupBy,
    // expanded
    toggleRowExpanded,
    rows,
    preGroupedRows,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: filter?.pageIndex ?? 0,
        pageSize: pagination?.pageSize ?? 50,
        groupBy: grouping,
        expanded,
      },
      // Ensure pageSize is always a valid number
      pageSize: pagination?.pageSize ?? 50,
      // Keep expanded state controlled externally
      useControlledState: (state) => {
        return {
          ...state,
          pageIndex: filter?.pageIndex ?? state.pageIndex,
          groupBy: grouping,
        };
      },
      autoResetExpanded: false,
      autoResetPage: false,
      autoResetGroupBy: false,
    },
    useFilters,
    useGlobalFilter,
    useGroupBy,
    useSortBy,
    useExpanded,
    usePagination
  );

  // Sync external expanded state changes back out
  useEffect(() => {
    if (setExpanded) setExpanded(tableExpanded);
  }, [tableExpanded]);

  const getCell = (cell, row) => {
    return (
      <Text size="sm" truncate>
        {cell.render("Cell")}
      </Text>
    );
  };

  const numbers = Array(50)
    .fill()
    .map((_, i) => i + 1);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <Box
        className="h-scroll-table-container"
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          marginBottom: 0,
         
        }}
        sx={(theme) => ({
          borderColor: theme.colorScheme === 'dark' ? `${theme.colors.dark[4]}` : `${theme.colors.gray[2]}`,
          borderWidth: 1,
          borderStyle: "solid",
          backgroundColor: theme.colorScheme === 'dark' ? `${theme.colors.dark[8]}` : `${theme.colors.white}`,
        })}
      >
        <Table
        withRowBorders
        withColumnBorders
        highlightOnHover
        size="sm"
        style={{ marginBottom: 0 }}
        className="h-scroll-table-first-column"
        stickyHeader
        stickyHeaderOffset={0}
        {...getTableProps()}
      >
        <thead>
          {headerGroups.map((headerGroup, idx) => (
            <tr key={idx} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, i) => (
                <Th
                  column={column}
                  onSort={() => logger.log("onsort")}
                  key={i}
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  reversed={column.isSortedDesc ? false : true}
                  sorted={column.isSorted ? true : undefined}
                  style={{
                    cursor: "pointer",
                    minWidth: column.isGrouped ? 350 : `${column.totalWidth}px`,
                  }}
                  className={idx === 0 ? [`${classes.tablecell}`,classes.firstColumn] : [`${classes.tablecell}`]}

                >
                  <div>
                    {column.render("Header")}
                  </div>
                </Th>
              ))}
            </tr>
          ))}
        </thead>

        {loading ? (
          <tbody>
            {numbers.map((i) => (
              <tr key={i}>
                {columns.map((col, idx) => (
                  <td key={idx}>
                    <Skeleton height={8} my="sm" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        ) : (
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              const checked = selectedItems?.find(
                (val) => val.id === row.original?.id
              )
                ? true
                : false;

              return (
                <tr
                  key={row.id}
                  className={checked ? "selected" : ""}
                  {...row.getRowProps()}
                >
                  {row.cells.map((cell,idx) => (
                    <td

                      key={cell.column.id}
                      onClick={() => onItemClick(row)}
                      className={checked ? "cellChecked " + classes.tablecell : classes.tablecell}
                      style={{ verticalAlign: "top"}}
                      {...cell.getCellProps()}
                    >
                      {cell.isGrouped ? (
                        // Grouped cell — show expander + row count
                        <UnstyledButton
                          size="sm"
                          onClick={row.getToggleRowExpandedProps().onClick}
                          style={{
                            cursor: row.canExpand ? "pointer" : "normal",
                          }}
                        >
                          <Group>
                            {row.isExpanded ? (
                              <IconChevronDown
                                stroke={AppConstants.ICON_STROKE_WIDTH}
                                size={AppConstants.ICON_SIZE}
                              />
                            ) : (
                              <IconChevronRight
                                stroke={AppConstants.ICON_STROKE_WIDTH}
                                size={AppConstants.ICON_SIZE}
                              />
                            )}
                            {cell.value ? (
                              getCell(cell, row)
                            ) : (
                              <Text c="dimmed">No value</Text>
                            )}
                            <Badge>{row.subRows.length}</Badge>
                          </Group>
                        </UnstyledButton>
                      ) : cell.isAggregated ? (
                        // Aggregated cell
                        cell.render("Aggregated")
                      ) : cell.isPlaceholder ? null : (
                        // Regular cell
                        getCell(cell, row)
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        )}
      </Table>
      
     
    </Box>
    {showPagination ? (
        <Paper style={{ flexShrink: 0, backgroundColor: "transparent" }}>
          {loading ? (
            <Group className={"pagination-footer"} py="sm">
              <Skeleton height={20} width={100} />
            </Group>
          ) : (
            <Group className={"pagination-footer"} py="sm">
              <Pagination
                size={"sm"}
                total={pageCount || 1}
                withEdges
                withPages={mobileMatch ? false : true}
                value={pageIndex + 1}
                onChange={(idx) => {
                  logger.log(idx);
                  setFilter({ ...filter, pageIndex: idx - 1 });
                  gotoPage(idx - 1);
                }}
              />
              <Group>
                <Text size="sm">
                  Page{" "}
                  <strong>
                    {pageIndex + 1} to {pageCount}
                  </strong>
                </Text>
              </Group>
            </Group>
          )}
        </Paper>
      ) : null}
    </div>
  );
};

export default MastersList;

const SampleItemMenu = (props) => {
  const defaultMenu = [
    {
      handler: () =>
        props.onMenuItemClick({
          type: SampleEventType.SAMPLE_VIEW,
          data: props.item,
        }),
      icon: <IconEye size={14} />,
      label: "View Sample",
    },
    {
      handler: () =>
        props.onMenuItemClick({
          type: SampleEventType.SAMPLE_ASSIGN_CAPTURES,
          data: props.item,
        }),
      icon: <IconDragDrop size={14} />,
      size: 14,
      label: "Style Guide Selections",
    },
    {
      handler: () =>
        props.onMenuItemClick({
          type: SampleEventType.SAMPLE_DELETE,
          data: props.item,
        }),
      color: "red",
      icon: <IconTrash size={14} />,
      size: 14,
      label: "Unassign Sample",
    },
  ];

  const menuItems = props.actionMenu ? props.actionMenu : defaultMenu;

  return (
    <Menu position="bottom-end">
      <Menu.Target>
        <ActionIcon>
          <IconDots />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {menuItems.map((val, idx) => (
          <Menu.Item
            key={idx}
            onClick={() => val.handler(props.item)}
            icon={val.icon}
          >
            {val.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};